import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../veevaGpt.css";

const markdownLinkRenderer = {
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};
import {
  FiSend,
  FiCpu,
  FiUser,
  FiChevronDown,
  FiPlus,
  FiChevronUp,
  FiTrash2,
  FiEdit2,
  FiSidebar,
  FiMessageSquare,
  FiFolder,
  FiCheckCircle,
} from "react-icons/fi";

function VeevaGptPage({
  userName,
  messages,
  onSubmitQuery,
  onClear,
  chatHistory,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  onRenameChat,
  projects,
  onCreateProject,
  onDeleteProject,
  onRenameProject,
  onNewChatInProject,
  activeProjectId,
  availableModels,
  selectedModel,
  onModelChange,
}) {
  const [inputValue, setInputValue] = useState("");
  const [isChatsSectionOpen, setIsChatsSectionOpen] = useState(true);
  const [isProjectsSectionOpen, setIsProjectsSectionOpen] = useState(true);
  const [renamingChatId, setRenamingChatId] = useState(null);
  const [renamingChatTitle, setRenamingChatTitle] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showNewProjectInput, setShowNewProjectInput] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [expandedProjects, setExpandedProjects] = useState({});
  const [renamingProjectId, setRenamingProjectId] = useState(null);
  const [renamingProjectName, setRenamingProjectName] = useState("");
  const chatScrollRef = useRef(null);
  const inputRef = useRef(null);
  const renameChatInputRef = useRef(null);
  const newProjectInputRef = useRef(null);
  const renameProjectInputRef = useRef(null);

  const hasMessages = messages && messages.length > 0;

  // Chats not assigned to any project
  const ungroupedChats = (chatHistory || []).filter((c) => !c.project_id);

  const defaultPrompts = [
    "What are the terminology differences between Vault CRM and Veeva CRM?",
    "How do I manage permission sets and object controls in Vault CRM?",
    "Which features are deprecated in Vault CRM?",
  ];

  const handlePromptClick = (prompt) => {
    onSubmitQuery(prompt);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onSubmitQuery(trimmed);
    setInputValue("");
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.altKey && !event.ctrlKey) {
      event.preventDefault();
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      onSubmitQuery(trimmed);
      setInputValue("");
    }
  };

  const startRenamingChat = (chatId, currentTitle) => {
    setRenamingChatId(chatId);
    setRenamingChatTitle(currentTitle);
  };

  const submitRenameChat = () => {
    const trimmed = renamingChatTitle.trim();
    if (trimmed && onRenameChat) {
      onRenameChat(renamingChatId, trimmed);
    }
    setRenamingChatId(null);
    setRenamingChatTitle("");
  };

  const cancelRenameChat = () => {
    setRenamingChatId(null);
    setRenamingChatTitle("");
  };

  // Project helpers
  const toggleProjectExpand = (projectId) => {
    setExpandedProjects((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const handleNewProjectSubmit = () => {
    const trimmed = newProjectName.trim();
    if (trimmed && onCreateProject) {
      onCreateProject(trimmed);
    }
    setNewProjectName("");
    setShowNewProjectInput(false);
  };

  const handleNewProjectKeyDown = (e) => {
    if (e.key === "Enter") handleNewProjectSubmit();
    else if (e.key === "Escape") {
      setShowNewProjectInput(false);
      setNewProjectName("");
    }
  };

  const startRenamingProject = (projectId, currentName) => {
    setRenamingProjectId(projectId);
    setRenamingProjectName(currentName);
  };

  const submitRenameProject = () => {
    const trimmed = renamingProjectName.trim();
    if (trimmed && onRenameProject) {
      onRenameProject(renamingProjectId, trimmed);
    }
    setRenamingProjectId(null);
    setRenamingProjectName("");
  };

  const cancelRenameProject = () => {
    setRenamingProjectId(null);
    setRenamingProjectName("");
  };

  useEffect(() => {
    if (!chatScrollRef.current) return;
    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!inputRef.current) return;
    const style = window.getComputedStyle(inputRef.current);
    const lineHeight = Number.parseFloat(style.lineHeight) || 20;
    const baseHeight = 80;
    const maxLines = 6;
    const maxHeight = baseHeight + lineHeight * (maxLines - 1);
    inputRef.current.style.height = "auto";
    const contentHeight = inputRef.current.scrollHeight;
    const nextHeight = Math.max(baseHeight, Math.min(contentHeight, maxHeight));
    inputRef.current.style.height = `${nextHeight}px`;
    inputRef.current.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
  }, [inputValue]);

  useEffect(() => {
    if (renamingChatId && renameChatInputRef.current) {
      renameChatInputRef.current.focus();
      renameChatInputRef.current.select();
    }
  }, [renamingChatId]);

  useEffect(() => {
    if (showNewProjectInput && newProjectInputRef.current) {
      newProjectInputRef.current.focus();
    }
  }, [showNewProjectInput]);

  useEffect(() => {
    if (renamingProjectId && renameProjectInputRef.current) {
      renameProjectInputRef.current.focus();
      renameProjectInputRef.current.select();
    }
  }, [renamingProjectId]);

  return (
    <div className={`veeva-gpt-layout${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
      {/* Left Sidebar */}
      <aside className={`veeva-gpt-sidebar${sidebarCollapsed ? " collapsed" : ""}`}>
        <div className="veeva-gpt-sidebar-header">
          <span className="veeva-gpt-sidebar-brand">
            <span className="veeva-gpt-vg-logo brand"><FiMessageSquare /></span>
            Veeva <span className="brand-accent">GPT</span>
          </span>
          <button
            className="veeva-gpt-sidebar-toggle"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <FiSidebar />
          </button>
        </div>

        <nav className="veeva-gpt-sidebar-nav">
          <button className="veeva-gpt-sidebar-item" onClick={onNewChat} title="New Chat">
            <FiPlus className="veeva-gpt-sidebar-icon" />
            <span className="veeva-gpt-sidebar-label">New Chat</span>
          </button>
          <button
            className="veeva-gpt-sidebar-item"
            onClick={() => setShowNewProjectInput(true)}
            title="New Project"
          >
            <FiFolder className="veeva-gpt-sidebar-icon" />
            <span className="veeva-gpt-sidebar-label">New Project</span>
          </button>
        </nav>

        <div className="veeva-gpt-sidebar-chats">
          {/* Projects Section */}
          <button
            className="veeva-gpt-chats-toggle"
            onClick={() => setIsProjectsSectionOpen((prev) => !prev)}
          >
            <span className="veeva-gpt-chats-title">Projects</span>
            {isProjectsSectionOpen ? (
              <FiChevronUp className="veeva-gpt-chats-chevron" />
            ) : (
              <FiChevronDown className="veeva-gpt-chats-chevron" />
            )}
          </button>
          {isProjectsSectionOpen && (
            <div className="veeva-gpt-projects-list">
              {showNewProjectInput && (
                <div className="veeva-gpt-new-project-input-row">
                  <input
                    ref={newProjectInputRef}
                    type="text"
                    className="veeva-gpt-new-project-input"
                    placeholder="Project name..."
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={handleNewProjectKeyDown}
                    onBlur={() => {
                      if (!newProjectName.trim()) setShowNewProjectInput(false);
                    }}
                  />
                  <button
                    className="veeva-gpt-new-project-confirm"
                    onClick={handleNewProjectSubmit}
                    disabled={!newProjectName.trim()}
                    title="Create project"
                  >
                    <FiCheckCircle />
                  </button>
                </div>
              )}
              {(projects || []).length === 0 && !showNewProjectInput && (
                <p className="veeva-gpt-chats-empty">No projects yet</p>
              )}
              {(projects || []).map((project) => {
                const isExpanded = expandedProjects[project.id] ?? false;
                const projectChats = (chatHistory || []).filter((c) => c.project_id === project.id);
                return (
                  <div key={project.id} className={`veeva-gpt-project-folder ${activeProjectId === project.id ? "active" : ""}`}>
                    <div className="veeva-gpt-project-header">
                      {renamingProjectId === project.id ? (
                        <div className="veeva-gpt-rename-input-row">
                          <input
                            ref={renameProjectInputRef}
                            type="text"
                            className="veeva-gpt-rename-input"
                            value={renamingProjectName}
                            onChange={(e) => setRenamingProjectName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") submitRenameProject();
                              else if (e.key === "Escape") cancelRenameProject();
                            }}
                            onBlur={submitRenameProject}
                          />
                        </div>
                      ) : (
                        <button
                          className="veeva-gpt-project-toggle"
                          onClick={() => toggleProjectExpand(project.id)}
                          title={project.name}
                        >
                          {isExpanded ? <FiChevronDown className="veeva-gpt-project-chevron" /> : <FiChevronDown className="veeva-gpt-project-chevron collapsed" />}
                          <FiFolder className="veeva-gpt-project-icon" />
                          <span className="veeva-gpt-project-name">{project.name}</span>
                          <span className="veeva-gpt-project-count">{projectChats.length}</span>
                        </button>
                      )}
                      <div className="veeva-gpt-project-actions">
                        <button
                          className="veeva-gpt-project-action-btn"
                          onClick={(e) => { e.stopPropagation(); onNewChatInProject && onNewChatInProject(project.id); }}
                          title="New chat in this project"
                        >
                          <FiPlus />
                        </button>
                        <button
                          className="veeva-gpt-project-action-btn"
                          onClick={(e) => { e.stopPropagation(); startRenamingProject(project.id, project.name); }}
                          title="Rename project"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="veeva-gpt-project-action-btn danger"
                          onClick={(e) => { e.stopPropagation(); onDeleteProject && onDeleteProject(project.id); }}
                          title="Delete project"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="veeva-gpt-project-chats">
                        {projectChats.length === 0 && (
                          <p className="veeva-gpt-chats-empty">No chats in this project</p>
                        )}
                        {projectChats.map((chat) => (
                          <div key={chat.id} className={`veeva-gpt-chat-item-row ${chat.active ? "active" : ""}`}>
                            {renamingChatId === chat.id ? (
                              <div className="veeva-gpt-rename-input-row">
                                <input
                                  ref={renameChatInputRef}
                                  type="text"
                                  className="veeva-gpt-rename-input"
                                  value={renamingChatTitle}
                                  onChange={(e) => setRenamingChatTitle(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") submitRenameChat();
                                    else if (e.key === "Escape") cancelRenameChat();
                                  }}
                                  onBlur={submitRenameChat}
                                />
                              </div>
                            ) : (
                              <>
                                <button
                                  className={`veeva-gpt-chat-item project-chat ${chat.active ? "active" : ""}`}
                                  onClick={() => onSelectChat && onSelectChat(chat.id)}
                                  title={chat.title}
                                >
                                  {chat.title}
                                </button>
                                <div className="veeva-gpt-chat-actions">
                                  <button
                                    className="veeva-gpt-chat-action-btn"
                                    onClick={(e) => { e.stopPropagation(); startRenamingChat(chat.id, chat.title); }}
                                    title="Rename chat"
                                  >
                                    <FiEdit2 />
                                  </button>
                                  <button
                                    className="veeva-gpt-chat-action-btn danger"
                                    onClick={(e) => { e.stopPropagation(); onDeleteChat && onDeleteChat(chat.id); }}
                                    title="Delete chat"
                                  >
                                    <FiTrash2 />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Ungrouped Chats Section */}
          <button
            className="veeva-gpt-chats-toggle"
            onClick={() => setIsChatsSectionOpen((prev) => !prev)}
          >
            <span className="veeva-gpt-chats-title">Chats</span>
            {isChatsSectionOpen ? (
              <FiChevronUp className="veeva-gpt-chats-chevron" />
            ) : (
              <FiChevronDown className="veeva-gpt-chats-chevron" />
            )}
          </button>
          {isChatsSectionOpen && (
            <div className="veeva-gpt-chats-list">
              {ungroupedChats.length === 0 && (
                <p className="veeva-gpt-chats-empty">No chats yet</p>
              )}
              {ungroupedChats.map((chat) => (
                <div key={chat.id} className={`veeva-gpt-chat-item-row ${chat.active ? "active" : ""}`}>
                  {renamingChatId === chat.id ? (
                    <div className="veeva-gpt-rename-input-row">
                      <input
                        ref={renameChatInputRef}
                        type="text"
                        className="veeva-gpt-rename-input"
                        value={renamingChatTitle}
                        onChange={(e) => setRenamingChatTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") submitRenameChat();
                          else if (e.key === "Escape") cancelRenameChat();
                        }}
                        onBlur={submitRenameChat}
                      />
                    </div>
                  ) : (
                    <>
                      <button
                        className={`veeva-gpt-chat-item ${chat.active ? "active" : ""}`}
                        onClick={() => onSelectChat && onSelectChat(chat.id)}
                        title={chat.title}
                      >
                        {chat.title}
                      </button>
                      <div className="veeva-gpt-chat-actions">
                        <button
                          className="veeva-gpt-chat-action-btn"
                          onClick={(e) => { e.stopPropagation(); startRenamingChat(chat.id, chat.title); }}
                          title="Rename chat"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="veeva-gpt-chat-action-btn danger"
                          onClick={(e) => { e.stopPropagation(); onDeleteChat && onDeleteChat(chat.id); }}
                          title="Delete chat"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </aside>

      {/* Main Content */}
      <main className="veeva-gpt-main">
        {!hasMessages ? (
          /* Empty/Welcome State */
          <div className="veeva-gpt-welcome">
            <div className="veeva-gpt-welcome-hero">
              <div className="veeva-gpt-welcome-icon-ring">
                <span className="veeva-gpt-vg-logo lg"><FiMessageSquare /></span>
              </div>
              <h1 className="veeva-gpt-greeting">
                Great to see you, {userName || "User"}!
              </h1>
              <p className="veeva-gpt-greeting-sub">Everything about Veeva CRM to Vault CRM migration—ask me anything!</p>
            </div>

            <div className="veeva-gpt-default-prompts">
              <div className="veeva-gpt-prompts-row">
                <button className="veeva-gpt-prompt-chip" onClick={() => handlePromptClick(defaultPrompts[0])} type="button">{defaultPrompts[0]}</button>
                <button className="veeva-gpt-prompt-chip" onClick={() => handlePromptClick(defaultPrompts[1])} type="button">{defaultPrompts[1]}</button>
              </div>
              <div className="veeva-gpt-prompts-row">
                <button className="veeva-gpt-prompt-chip" onClick={() => handlePromptClick(defaultPrompts[2])} type="button">{defaultPrompts[2]}</button>
              </div>
            </div>

            <div className="veeva-gpt-input-container">
              <div className="veeva-gpt-input-box">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  className="veeva-gpt-textarea"
                  placeholder="Ask anything..."
                  aria-label="Chat input"
                  rows={2}
                />
                <div className="veeva-gpt-input-toolbar">
                  <div className="veeva-gpt-toolbar-left">
                    {availableModels && availableModels.length > 1 ? (
                      <label className="veeva-gpt-model-label veeva-gpt-model-select-wrap">
                        <FiCpu className="veeva-gpt-model-icon" />
                        <select
                          className="veeva-gpt-model-select"
                          value={selectedModel || ""}
                          onChange={(e) => onModelChange && onModelChange(e.target.value)}
                          aria-label="Select model"
                        >
                          {availableModels.map((m) => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <span className="veeva-gpt-model-label">
                        <FiCpu className="veeva-gpt-model-icon" />
                        {availableModels && availableModels[0] ? availableModels[0].label : "GPT-4.1"}
                      </span>
                    )}
                  </div>
                  <div className="veeva-gpt-toolbar-right">
                    <button
                      type="button"
                      className="veeva-gpt-send-btn"
                      onClick={handleSubmit}
                      disabled={!inputValue.trim()}
                      aria-label="Send message"
                    >
                      <FiSend />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Chat Thread View */
          <div className="veeva-gpt-chat-view">
            <div className="veeva-gpt-messages" ref={chatScrollRef}>
              <div className="veeva-gpt-thread">
                {messages.map((message) => {
                  if (message.role === "user") {
                    return (
                      <div key={message.id} className="veeva-gpt-user-row">
                        <div className="veeva-gpt-user-bubble">{message.text}</div>
                        <div className="veeva-gpt-user-avatar">
                          <FiUser />
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={message.id} className="veeva-gpt-assistant-row">
                      <div className="veeva-gpt-assistant-avatar">
                        <span className="veeva-gpt-vg-logo"><FiMessageSquare /></span>
                      </div>
                      <div className="veeva-gpt-assistant-content">
                        {message.isLoading ? (
                          <div className="veeva-gpt-loading-block">
                            {!message.answer && (!message.reasoning || message.reasoning.length === 0) && (
                              <div className="veeva-gpt-loading-dots">
                                <span className="veeva-gpt-dot" />
                                <span className="veeva-gpt-dot" />
                                <span className="veeva-gpt-dot" />
                              </div>
                            )}
                            {message.reasoning?.length > 0 && (
                              <details
                                className={`veeva-gpt-reasoning-dropdown${!message.answer ? " veeva-gpt-reasoning-dropdown--live" : ""}`}
                                open={!message.answer ? true : undefined}
                              >
                                <summary>
                                  {!message.answer && <span className="veeva-gpt-reasoning-pulse" />}
                                  <FiChevronDown className="veeva-gpt-reasoning-chevron" />
                                  Reasoning
                                </summary>
                                <div className="veeva-gpt-reasoning-body">
                                  {message.reasoning.map((step, index) => (
                                    <div key={`${message.id}-live-r-${index}`} className="veeva-gpt-reasoning-step">
                                      <FiCheckCircle className="veeva-gpt-reasoning-icon" />
                                      <span>{step}</span>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                            {message.answer && (
                              <div className="veeva-gpt-answer-card is-streaming">
                                <div className="veeva-gpt-markdown">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownLinkRenderer}>{message.answer || ""}</ReactMarkdown>
                                </div>
                                {message.highlights?.length > 0 && (
                                  <ul className="veeva-gpt-highlights">
                                    {message.highlights.map((item, idx) => (
                                      <li key={`${message.id}-sh-${idx}`}>{item}</li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            {message.reasoning?.length > 0 && (
                              <details className="veeva-gpt-reasoning-dropdown">
                                <summary>
                                  <FiChevronDown className="veeva-gpt-reasoning-chevron" />
                                  Reasoning
                                </summary>
                                <div className="veeva-gpt-reasoning-body">
                                  {message.reasoning.map((step, index) => (
                                    <div key={`${message.id}-r-${index}`} className="veeva-gpt-reasoning-step">
                                      <FiCheckCircle className="veeva-gpt-reasoning-icon" />
                                      <span>{step}</span>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                            <div className="veeva-gpt-answer-card">
                              <div className="veeva-gpt-markdown">
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownLinkRenderer}>{message.answer || ""}</ReactMarkdown>
                              </div>
                              {message.highlights?.length > 0 && (
                                <ul className="veeva-gpt-highlights">
                                  {message.highlights.map((item, idx) => (
                                    <li key={`${message.id}-h-${idx}`}>{item}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="veeva-gpt-chat-input-area">
              <div className="veeva-gpt-input-container">
                <div className="veeva-gpt-input-box">
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    className="veeva-gpt-textarea"
                    placeholder="Ask anything..."
                    aria-label="Chat input"
                    rows={1}
                  />
                  <div className="veeva-gpt-input-toolbar">
                    <div className="veeva-gpt-toolbar-left">
                      <span className="veeva-gpt-model-label">
                        <FiCpu className="veeva-gpt-model-icon" />
                        GPT-4.1
                      </span>
                    </div>
                    <div className="veeva-gpt-toolbar-right">
                      <button
                        type="button"
                        className="veeva-gpt-send-btn"
                        onClick={handleSubmit}
                        disabled={!inputValue.trim()}
                        aria-label="Send message"
                      >
                        <FiSend />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default VeevaGptPage;
